import React, { useState, useEffect, useRef } from "react";
import "./nav.css";
import ResumePDF from "../../assets/Resume.pdf";

// every <section id> on the page, in document order.
// primary ones stay in the top bar; the rest live in the side rail (desktop)
// and in the hamburger menu (mobile), so nothing is unreachable.
const links = [
    { href: "#", label: "Home", primary: true },
    { href: "#about", label: "About", primary: true },
    { href: "#services", label: "Services" },
    { href: "#highlights", label: "Highlights" },
    { href: "#experience", label: "Experience" },
    { href: "#projects", label: "Projects", primary: true },
    { href: "#prototypes", label: "Prototypes" },
    { href: "#skills", label: "Skills", primary: true },
    { href: "#topShowcase", label: "Systems" },
    { href: "#contact", label: "Connect", primary: true },
];

const Nav = () => {
    const [activeNav, setActiveNav] = useState("#");
    const navRef = useRef(null);
    const [timeString, setTimeString] = useState("");
    const [isHidden, setIsHidden] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const lastScrollYRef = useRef(0);
    const tickingRef = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveNav("#" + entry.target.id);
                    }
                });
            },
            { rootMargin: "-50% 0px -50% 0px" }
        );

        const sections = document.querySelectorAll("section");
        sections.forEach((section) => observer.observe(section));

        return () => {
            sections.forEach((section) => observer.unobserve(section));
        };
    }, []);

    useEffect(() => {
        const formatTime = () => {
            const now = new Date();
            const hh = String(now.getHours()).padStart(2, "0");
            const mm = String(now.getMinutes()).padStart(2, "0");
            return `${hh}:${mm}`;
        };
        setTimeString(formatTime());
        const interval = setInterval(() => setTimeString(formatTime()), 1000 * 30);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const onScroll = () => {
            if (!tickingRef.current) {
                window.requestAnimationFrame(() => {
                    const currentY = window.scrollY || window.pageYOffset;
                    const lastY = lastScrollYRef.current;

                    if (currentY > lastY && currentY > 80) {
                        setIsHidden(true);
                    } else {
                        setIsHidden(false);
                    }

                    lastScrollYRef.current = currentY;
                    tickingRef.current = false;
                });
                tickingRef.current = true;
            }
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth > 900 && menuOpen) setMenuOpen(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [menuOpen]);

    return (
        <>
            <nav ref={navRef} className={`top-nav${isHidden ? " hidden" : ""}${menuOpen ? " open" : ""}`}>
                <div className="top-left">
                    <span className="top-avatar" aria-hidden="true">HB</span>
                    <span className="top-user">
                        <span className="top-name">Harshit</span>
                        <span className="top-status"><span className="top-dot" />Online</span>
                    </span>
                    <span className="top-time" aria-label="current time">{timeString}</span>
                </div>
                <button
                    className="menu-toggle"
                    aria-label="Toggle navigation menu"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((v) => !v)}
                >
                    ☰
                </button>
                <div className="top-links">
                    {links.map(({ href, label, primary }) => (
                        <a
                            key={href}
                            href={href}
                            onClick={() => { setActiveNav(href); setMenuOpen(false); }}
                            className={`${primary ? "" : "secondary "}${activeNav === href ? "active" : ""}`}
                        >
                            {label}
                        </a>
                    ))}
                    <a className="nav-resume" href={ResumePDF} target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)}>
                        Resume
                    </a>
                </div>
            </nav>

            <aside className="section-rail" aria-label="Section navigation">
                {links.map(({ href, label }) => (
                    <a
                        key={href}
                        href={href}
                        onClick={() => setActiveNav(href)}
                        className={`rail-dot${activeNav === href ? " active" : ""}`}
                        aria-label={label}
                        aria-current={activeNav === href ? "true" : undefined}
                    >
                        <span className="rail-tip">{label}</span>
                        <span className="rail-mark" />
                    </a>
                ))}
            </aside>
        </>
    );
};

export default Nav;
